import { z } from "zod";

import { BRAND_MARKUP } from "../../constants";
import { defineModel } from "../types";

// TODO: Verify ElevenLabs pricing - assumed same as TTS (~$0.30 per 1K chars)
const COST_PER_CHAR_MICRODOLLARS = 0.0003 * 1_000_000;

const speakerSchema = z.object({
  label: z
    .string()
    .describe("Speaker label matching prompt lines (e.g. ALICE)"),
  voiceId: z.string().describe("ElevenLabs voice ID for this speaker"),
});

const elevenlabsDialogueParams = z
  .object({
    speakers: z.array(speakerSchema).default([]),
    script: z.string().default(""),
    stability: z.number().min(0).max(1).optional(),
  })
  .strip();

export const elevenlabsDialogue = defineModel({
  key: "elevenlabs-dialogue",
  stepType: "dialogue",
  spec: {
    label: "ElevenLabs",
    description: "Multi-speaker dialogue with natural conversation flow",
    icon: "elevenlabs-icon.svg",
    tags: ["elevenlabs"],
    features: { prompts: true } as const,
    options: {},
    params: elevenlabsDialogueParams,
  },
  providers: [
    {
      provider: "elevenlabs",
      modelId: "elevenlabs-dialogue",
      estimateCost(params) {
        const charCount = (params.script ?? "").length;
        return Math.round(
          charCount * COST_PER_CHAR_MICRODOLLARS * (1 + BRAND_MARKUP),
        );
      },
      toPayload(params) {
        // Parse script into dialogue inputs
        const lines = (params.script ?? "")
          .split("\n")
          .filter((line) => line.trim());
        const speakerMap = new Map(
          (params.speakers ?? []).map((speaker) => [
            speaker.label.toUpperCase(),
            speaker.voiceId,
          ]),
        );

        const inputs: { text: string; voiceId: string }[] = [];

        for (const line of lines) {
          const match = /^([A-Z0-9_]+):\s*(.+)$/i.exec(line);
          if (match) {
            const [, label, text] = match;
            const voiceId = speakerMap.get((label ?? "").toUpperCase());
            if (voiceId && text) {
              inputs.push({ text: text.trim(), voiceId });
            }
          }
        }

        return {
          inputs,
          stability: params.stability,
        };
      },
    },
  ],
});
